export interface JoinResponse {
  serverUrl: string;
  token: string;
  identity: string;
  isHost: boolean;
  telemetryKey?: string;
  /** The display name as the server stored it, and the key to this call's chat history. */
  name?: string;
  chatKey?: string;
  /** The host may record, and the server has recording set up. */
  canRecord?: boolean;
  /** The host may turn on the in-call assistant. */
  canAssist?: boolean;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number) {
    super(code);
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.error ?? 'http_error', res.status);
  return data as T;
}

export interface RecordingState {
  id: string;
  status: string;
  ready: boolean;
  url?: string | null;
}

export const api = {
  createRoom: () => request<{ code: string; hostKey: string }>('/api/rooms', { method: 'POST' }),
  getRoom: (code: string) => request<{ code: string }>(`/api/rooms/${encodeURIComponent(code)}`),
  recording: (code: string, hostKey: string, action: 'start' | 'stop') =>
    request<{ recording: RecordingState }>(`/api/rooms/${encodeURIComponent(code)}/recording`, {
      method: 'POST',
      body: JSON.stringify({ hostKey, action }),
    }),
  recordingStatus: (code: string, hostKey: string) =>
    request<{ recording: RecordingState | null }>(`/api/rooms/${encodeURIComponent(code)}/recording/status`, {
      method: 'POST',
      body: JSON.stringify({ hostKey }),
    }),
  join: (code: string, name: string, hostKey: string | null) =>
    request<JoinResponse>(`/api/rooms/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify({ name, hostKey }),
    }),
  endRoom: (code: string, hostKey: string) =>
    request<{ ok: true }>(`/api/rooms/${encodeURIComponent(code)}/end`, {
      method: 'POST',
      body: JSON.stringify({ hostKey }),
    }),
};

/** Mirrors the server: letters only, 12 of them, grouped 4-4-4. */
export function normalizeRoomCode(input: string): string | null {
  const fromUrl = input.trim().split('/').filter(Boolean).pop() ?? '';
  const letters = fromUrl.toLowerCase().replace(/[^a-z]/g, '');
  if (letters.length !== 12) return null;
  return `${letters.slice(0, 4)}-${letters.slice(4, 8)}-${letters.slice(8)}`;
}
